import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CHAPTERS, NEWS } from '../mock';
import { MapPin, Calendar, Mail, Phone, ArrowLeft, Star, Building2, Facebook, Instagram, Twitter, Clock, Sparkles, Users2, Newspaper, Tag } from 'lucide-react';

function ChapterSocials({ socials }) {
  const items = [
    socials.facebook && { Icon: Facebook, href: socials.facebook, label: 'Facebook' },
    socials.instagram && { Icon: Instagram, href: socials.instagram, label: 'Instagram' },
    socials.twitter && { Icon: Twitter, href: socials.twitter, label: 'Twitter' },
  ].filter(Boolean);
  if (items.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-cbtu-muted">
        <Clock size={12} className="text-gold" /> Social media coming soon
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {items.map(({ Icon, href, label }) => (
        <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={`${label} (opens in new tab)`} className="p-2 border border-gold/40 text-gold hover:bg-gold hover:text-cbtu-black transition-colors rounded-sm">
          <Icon size={14} />
        </a>
      ))}
    </div>
  );
}

export default function Chapter() {
  const { slug } = useParams();
  const chapter = CHAPTERS.find((c) => c.slug === slug);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/chapter-photos/${slug}`).then((r) => {
      if (cancelled) return;
      const items = (r.data?.items || []).map((p) => ({
        src: `${base}${p.src}`,
        caption: p.caption,
        instagram: p.instagram,
        handle: p.handle,
      }));
      setUploadedPhotos(items);
    }).catch(() => { /* keep seeds only */ });
    return () => { cancelled = true; };
  }, [slug]);

  if (!chapter) return <Navigate to="/chapters" replace />;
  const chapterNews = NEWS.filter((n) => n.chapterSlug === slug);
  const allPhotos = [...uploadedPhotos, ...(chapter.photos || [])];

  return (
    <div className="fade-in-up">
      <section className="banner-bg section-noise">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <Link to="/chapters" className="text-gold text-sm inline-flex items-center gap-2 mb-6 hover:text-gold-bright"><ArrowLeft size={14} /> All Chapters</Link>
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="chip mb-3">{chapter.state} · Est. {chapter.founded}</div>
              <h1 className="font-display text-5xl md:text-6xl text-cbtu-cream leading-none">
                CBTU <span className="gold-text-fill">{chapter.name.toUpperCase()}</span>
              </h1>
              <p className="text-cbtu-text/90 text-lg leading-relaxed mt-4 max-w-2xl">{chapter.summary}</p>
            </div>
            <aside className="card-dark p-6 rounded-md">
              <div className="font-heading tracking-widest text-gold text-xs mb-3">CHAPTER LEADERSHIP</div>
              <div className="font-display text-2xl text-cbtu-cream">{chapter.president}</div>
              <div className="text-gold text-sm">{chapter.presidentTitle}</div>
              {chapter.union && <div className="text-xs text-cbtu-muted mt-1">{chapter.union}</div>}
              <div className="gold-divider my-4"></div>
              <ul className="space-y-2.5 text-sm text-cbtu-cream">
                {chapter.venue && <li className="flex items-start gap-2"><MapPin size={14} className="text-gold mt-0.5" /> {chapter.venue}</li>}
                {chapter.meeting && <li className="flex items-center gap-2"><Calendar size={14} className="text-gold" /> {chapter.meeting}</li>}
                {chapter.email && <li className="flex items-center gap-2"><Mail size={14} className="text-gold" /> <a href={`mailto:${chapter.email}`} className="hover:text-gold-bright break-all">{chapter.email}</a></li>}
                {chapter.phone && <li className="flex items-center gap-2"><Phone size={14} className="text-gold" /> {chapter.phone}</li>}
              </ul>
              <div className="gold-divider my-4"></div>
              <div className="font-heading tracking-widest text-gold text-xs mb-2">FOLLOW THE CHAPTER</div>
              <ChapterSocials socials={chapter.socials || {}} />
            </aside>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        {chapter.featuredProgram && (
          <div className="mb-10">
            <div className="relative overflow-hidden rounded-md gold-border bg-gradient-to-br from-[#1f4a28] via-[#143018] to-[#0f1530] p-6 md:p-8">
              <div className="absolute -top-10 -right-10 w-48 h-48 bg-gold/10 rounded-full blur-3xl" aria-hidden></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="text-gold" size={18} />
                  <span className="chip">FEATURED PROGRAM</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-cbtu-cream mb-3">{chapter.featuredProgram.title}</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {chapter.featuredProgram.partners.map((p) => (
                    <span key={p} className="font-heading text-xs tracking-widest text-cbtu-black bg-gold px-3 py-1 rounded-sm">{p}</span>
                  ))}
                </div>
                <p className="text-cbtu-text/95 text-base md:text-lg leading-relaxed max-w-3xl">{chapter.featuredProgram.description}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  {chapter.email && <a href={`mailto:${chapter.email}?subject=${encodeURIComponent(chapter.featuredProgram.title + ' - Inquiry')}`} className="gold-button px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2"><Users2 size={14} /> Get Involved</a>}
                  <Link to="/contact" className="ghost-button px-5 py-2.5 rounded-sm text-sm">Partner with us</Link>
                </div>
              </div>
            </div>
          </div>
        )}
        {chapter.representedUnions && chapter.representedUnions.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4"><Building2 className="text-gold" size={18} /><h2 className="font-display text-2xl text-cbtu-cream">UNIONS REPRESENTED</h2></div>
            <div className="grid sm:grid-cols-2 gap-4">
              {chapter.representedUnions.map((u) => (
                <div key={u.name} className="card-dark p-5 rounded-md">
                  <div className="font-display text-xl gold-text-fill mb-1">{u.name}</div>
                  <p className="text-sm text-cbtu-text/90 leading-relaxed">{u.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4"><Star className="text-gold" size={18} fill="currentColor" /><h2 className="font-display text-2xl text-cbtu-cream">CURRENT INITIATIVES</h2></div>
            <ul className="space-y-3">
              {chapter.initiatives.map((i) => (
                <li key={i} className="card-dark p-4 rounded-md text-cbtu-text/90 flex items-start gap-3">
                  <Star size={14} className="text-gold mt-1 flex-shrink-0" fill="currentColor" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-4"><Building2 className="text-gold" size={18} /><h2 className="font-display text-2xl text-cbtu-cream">GET INVOLVED</h2></div>
            <div className="card-dark p-6 rounded-md">
              <p className="text-cbtu-text/90 mb-4">Whether you’re a rank-and-file member, a community partner, or a future organizer, the {chapter.name} chapter has a place for you.</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-cbtu-cream"><Calendar size={14} className="text-gold" /> Attend a meeting: {chapter.meeting}</div>
                <div className="flex items-center gap-2 text-cbtu-cream"><Mail size={14} className="text-gold" /> Email the chapter to be added to the list</div>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {chapter.email && <a href={`mailto:${chapter.email}`} className="gold-button px-5 py-2.5 rounded-sm text-sm">Email Chapter</a>}
                <Link to="/contact" className="ghost-button px-5 py-2.5 rounded-sm text-sm">Contact Region III</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {chapterNews.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-12" data-testid="chapter-news">
          <div className="flex items-center gap-2 mb-6">
            <Newspaper className="text-gold" size={18} />
            <h2 className="font-display text-2xl text-cbtu-cream">CHAPTER <span className="gold-text-fill">NEWS &amp; EVENTS</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chapterNews.map((n) => (
              <article key={n.id} className="card-dark rounded-md overflow-hidden flex flex-col">
                {n.img && (
                  <a href={n.img} target="_blank" rel="noreferrer" className="block aspect-[16/19] overflow-hidden bg-cbtu-black">
                    <img src={n.img} alt={n.title} className="w-full h-full object-cover hover:scale-[1.02] transition-transform" loading="lazy" />
                  </a>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <span className="chip inline-flex items-center gap-1"><Tag size={10} />{n.tag}</span>
                    <span className="text-cbtu-muted inline-flex items-center gap-1"><Calendar size={12} className="text-gold" />{n.date}</span>
                  </div>
                  <h3 className="font-heading text-lg text-cbtu-cream leading-snug mb-2">{n.title}</h3>
                  <p className="text-sm text-cbtu-text/85 flex-1">{n.excerpt}</p>
                  {n.instagram && (
                    <a href={n.instagram} target="_blank" rel="noopener noreferrer" className="mt-4 self-start inline-flex items-center gap-1.5 text-gold hover:text-gold-bright text-xs font-heading tracking-widest">
                      <Instagram size={13} /> FOLLOW {n.socialHandle || 'US'} ON INSTAGRAM →
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {allPhotos.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-16" data-testid="chapter-photos">
          <div className="flex items-center gap-2 mb-6">
            <Instagram className="text-gold" size={18} />
            <h2 className="font-display text-2xl text-cbtu-cream">CHAPTER <span className="gold-text-fill">PHOTOS</span></h2>
            {chapter.socials?.instagram && (
              <a href={chapter.socials.instagram} target="_blank" rel="noopener noreferrer" className="ml-2 text-gold hover:text-gold-bright text-xs font-heading tracking-widest">
                @CBTU_jax →
              </a>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {allPhotos.map((p) => (
              <figure key={p.src} className="card-dark rounded-md overflow-hidden group">
                <a href={p.src} target="_blank" rel="noreferrer" className="block overflow-hidden bg-cbtu-black">
                  <img src={p.src} alt={p.caption || `${chapter.name} chapter photo`} className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" />
                </a>
                <figcaption className="px-4 py-3 border-t border-gold/20 flex items-center justify-between gap-3">
                  <span className="text-cbtu-cream text-sm">{p.caption}</span>
                  {p.instagram && (
                    <a href={p.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gold hover:text-gold-bright text-[11px] font-heading tracking-widest" aria-label={`Follow ${p.handle || chapter.name} on Instagram`}>
                      <Instagram size={12} /> {p.handle || 'INSTAGRAM'}
                    </a>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
